import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import axios from "axios";
import { getStoredLang } from "../utils/i18n";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const AuthContext = createContext(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be within AuthProvider");
  return ctx;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(localStorage.getItem("ac_token"));
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState(false);
  const [retryCount, setRetryCount] = useState(0);

  const getHeaders = useCallback(() => ({
    headers: { Authorization: `Bearer ${token}` }
  }), [token]);

  useEffect(() => {
    let cancelled = false;
    setAuthError(false);
    if (token) {
      setLoading(true);
      axios.get(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` }, timeout: 15000 })
        .then(res => { if (!cancelled) { setUser(res.data); setLoading(false); } })
        .catch(error => {
          if (cancelled) return;
          if (error.response?.status === 401) {
            localStorage.removeItem("ac_token"); setToken(null); setUser(null);
          } else {
            // Network errors and server failures do not invalidate a session.
            setAuthError(true);
          }
          setLoading(false);
        });
    } else {
      setLoading(false);
    }
    return () => { cancelled = true; };
  }, [token, retryCount]);

  const login = async (email, password) => {
    const res = await axios.post(`${API}/auth/login`, { email, password });
    localStorage.setItem("ac_token", res.data.token);
    setToken(res.data.token);
    setUser(res.data.user);
    return res.data;
  };

  const register = async (username, email, password) => {
    const res = await axios.post(`${API}/auth/register`, { username, email, password });
    if (res.data.token) {
      localStorage.setItem("ac_token", res.data.token);
      setToken(res.data.token);
      setUser(res.data.user);
    }
    return res.data;
  };

  const logout = () => {
    localStorage.removeItem("ac_token");
    setToken(null);
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, token, loading, login, register, logout, getHeaders, API }}>
      {authError ? (
        <div role="alert" className="min-h-screen flex flex-col items-center justify-center gap-4 bg-[#0a0a0c] text-white p-6 text-center">
          <p>{getStoredLang() === "it" ? "Non riesco a verificare la sessione. La tua posizione è stata mantenuta: riprova quando la connessione è disponibile." : "Unable to verify your session. Your location has been preserved. Retry when the connection is available."}</p>
          <button className="rounded border border-amber-400 px-6 py-3" onClick={() => { setLoading(true); setAuthError(false); setRetryCount(n => n + 1); }}>
            {getStoredLang() === "it" ? "Riprova" : "Retry"}
          </button>
        </div>
      ) : children}
    </AuthContext.Provider>
  );
};
